<script lang="ts">
  import { page } from '$app/state';
  import { PUBLIC_ENVIRONMENT } from '$env/static/public';
  import { authReady, getCurrentUser, onAuthChange, refreshHandleClaim } from '$lib/firebase/auth/auth.svelte';
  import { jsonLdScriptTag, resolveHead, type PageHead } from '@core/seo';
  import { loadPersistedTheme } from '@core/theme';
  import '@picocss/pico';
  import { invalidate, invalidateAll } from '$app/navigation';
  import { afterNavigate, beforeNavigate } from '$app/navigation';
  import { capture, identify, reset } from '@core/product';
  import { CookieId, refreshSession, restoreSession, signOut } from '@shared/api';
  import { onMount } from 'svelte';
  import '../styles.scss';

  interface Props {
    children?: import('svelte').Snippet;
  }

  let { children }: Props = $props();

  const head = $derived(resolveHead(page.data['head'] as PageHead | undefined, page.url));

  beforeNavigate(() => capture('$pageleave'));

  afterNavigate((navigation) => {
    if (navigation.type !== 'enter') {
      capture('$pageview');
      invalidate('app:flags');
      // Detect mid-session divergence: server may have cleared the session cookie
      // (expired/revoked) while the client still holds a Firebase user in memory.
      // Running on every client navigation avoids the "sign-in page says signed-in
      // but Dashboard bounces back" trap that previously required a hard refresh.
      void syncSession();
    }
  });

  async function syncSession() {
    try {
      const hasSession = document.cookie.includes(`${CookieId.HasSession}=true`);
      const needsRefresh = document.cookie.includes(`${CookieId.NeedsSessionRefresh}=true`);
      await authReady;
      const user = getCurrentUser();

      if (user && !hasSession) await signOut();
      else if (!user && hasSession) await restoreSession();
      else if (user && needsRefresh) await refreshSession(user);
    } catch (err) {
      console.error('layout|session sync failed, signing out', err);
      await signOut();
    }
  }

  onMount(() => {
    let unsubscribe: (() => void) | undefined;
    let mounted = true;

    // Track the last observed auth state so we can detect cross-tab
    // transitions. `observed` gates out the first callback (which fires
    // with whatever Firebase already has cached, matching the SSR state)
    // — invalidating then would just re-run the loads we just rendered.
    let observed = false;
    let lastObservedUserId: string | undefined;

    onAuthChange(async (user) => {
      const newUserId = user?.uid;
      const isCrossTabTransition = observed && lastObservedUserId !== newUserId;
      observed = true;
      lastObservedUserId = newUserId;

      if (user) {
        const provider = user.providerData[0]?.providerId;
        const tokenResult = await user.getIdTokenResult();
        const plan = (tokenResult.claims['plan'] as string) ?? 'free';
        const handleClaim = tokenResult.claims['handle'];
        const handle = typeof handleClaim === 'string' && handleClaim.trim() ? handleClaim : undefined;
        const name = user.displayName?.trim() ? user.displayName : undefined;

        // Mirror the handle claim into the module-level cache so reactive
        // getAuthState() consumers see it without each having to await
        // getIdTokenResult themselves.
        await refreshHandleClaim(user);

        identify(user.uid, {
          email: user.email,
          name,
          handle,
          plan,
          environment: PUBLIC_ENVIRONMENT,
          email_verified: user.emailVerified,
          auth_provider: provider,
          created_at: user.metadata.creationTime,
        });
      } else {
        await refreshHandleClaim(null);
        reset();
      }

      // Cross-tab propagation: Firebase IndexedDB sync fires onAuthChange
      // in this tab when another tab signs in or out. Re-run loads so the
      // page can react to the new state — auth-required layouts redirect
      // out on sign-out, and routes like /sign-in?returnto=… redirect to
      // returnTo on sign-in (see src/routes/sign-in/+page.server.ts).
      if (isCrossTabTransition) {
        // Race fix: when the other tab signs in (especially as a different
        // user), Firebase's IndexedDB sync to this tab usually completes
        // BEFORE the originating tab's POST /api/sign-in has set the
        // origin-wide session cookie. invalidateAll() running here would
        // see a stale cookie state and the server load wouldn't redirect.
        // Mint our own cookie from this tab's freshly-synced ID token
        // first — refreshSession is safe to call on every transition
        // (no-op if email isn't verified; idempotent if a cookie already
        // exists from the other tab's call).
        if (user) await refreshSession(user);
        await invalidateAll();
      }
    }).then((unsub) => {
      if (mounted) unsubscribe = unsub;
      else unsub?.();
    });

    return () => {
      mounted = false;
      unsubscribe?.();
    };
  });

  onMount(async () => {
    await syncSession();

    loadPersistedTheme();
  });

  // Re-sync when the tab returns to the foreground. Covers the case
  // where a user leaves a tab open past the server cookie's TTL, or
  // signs out in another tab/device while this one is hidden. Without
  // this, the divergence is only caught on the next client navigation.
  onMount(() => {
    function onVisibilityChange() {
      if (document.visibilityState === 'visible') {
        void syncSession();
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  });
</script>

<svelte:head>
  <title>{head.title}</title>
  <meta name="description" content={head.description} />
  <link rel="canonical" href={head.canonical} />

  <meta property="og:title" content={head.ogTitle} />
  <meta property="og:description" content={head.ogDescription} />
  <meta property="og:url" content={head.ogUrl} />
  <meta property="og:type" content={head.ogType} />
  <meta property="og:image" content={head.ogImage} />

  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content={head.ogTitle} />
  <meta name="twitter:description" content={head.ogDescription} />
  <meta name="twitter:image" content={head.ogImage} />

  {#if head.noIndex}<meta name="robots" content="noindex,nofollow" />{/if}

  {#each head.jsonLd as ld, i (i)}
    <!-- eslint-disable-next-line svelte/no-at-html-tags -->
    {@html jsonLdScriptTag(ld)}
  {/each}
</svelte:head>

{@render children?.()}
